import { Component, ElementRef, NgZone, OnInit, ViewChild } from '@angular/core';
import { MessageNotificationService } from 'message-notification-service-lib';
import { Observable, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { IdentityStorage } from 'storage-service-lib';
import {
  IFieldMeta,
  SCRIPT_FIELDS_ENTITY_NAME,
  SCRIPT_FIELDS_ENTITY_NAME_LABEL_PAIR,
  SCRIPT_FIELDS_NAMESPACE,
} from '../../../interfaces/imetadata';
import { ContentControlToFieldMapService, SerializedFieldContext } from '../../../services/content-control-to-field-map.service';
import { ControllerService } from '../../../services/controller.service';
import { ActiveContentControlMonitor } from '../../../services/document-monitors/active-content-control-monitor';
import { ContentControlId } from '../../../services/document-monitors/content-control-id';
import { LoggingService } from '../../../services/logging.service';
import { systemDefaults } from '../../../utils/fieldconfighelper';
import { FieldContext } from '../../../utils/fieldcontext';
import { FieldType } from '../../../utils/fieldtype';
import { Tag, TagType } from '../../../utils/tag';
import { templateContext } from '../../../utils/template-context';
import { IBreadcrumbPath } from '../breadcrumbs/breadcrumbs.component';
import { IValidStateChangeProvider, ValidStateProvider } from '../valid-state-provider';

@Component({
  selector: 'app-script-fields',
  templateUrl: './script-fields.component.html',
  styleUrls: ['../field-selection.component.scss', '../../../../assets/css-imports/adv2-word-addin/generic-tab-with-form.scss'],
  standalone: false,
})
export class ScriptFieldsComponent implements OnInit, IValidStateChangeProvider {
  readonly FieldType = FieldType;
  breadcrumbs: IBreadcrumbPath = { pathItems: [] };
  field: IFieldMeta = { namespace: '', path: '', label: '', type: FieldType.STRING };
  pathPlaceHolder = '';
  absolutePath = '';

  @ViewChild('pathInput')
  pathInputElement: ElementRef | undefined;

  private activeField: SerializedFieldContext | undefined = undefined;
  private valid = new ValidStateProvider();
  private pathValueChangesSubject = new Subject<string>();
  private pathValueChanges$ = this.pathValueChangesSubject.asObservable();

  constructor(
    private activeContentControlMonitor: ActiveContentControlMonitor,
    private contentControlToFieldMapService: ContentControlToFieldMapService,
    private controllerService: ControllerService,
    private identityStorage: IdentityStorage,
    private messageNotificationService: MessageNotificationService,
    private ngZone: NgZone,
    private loggingService: LoggingService
  ) {
    activeContentControlMonitor.activeContentControlChanged$.subscribe((acc) => this.activeControlChanged(acc));
  }

  ngOnInit() {
    this.field.namespace = this.identityStorage.get().namespace;
    this.pathValueChanges$.pipe(debounceTime(400)).subscribe({
      next: (pathText) => {
        this.buildAbsolutePath(pathText);
      },
    });
  }

  get validStateChange$(): Observable<boolean> {
    return this.valid.validStateChange$;
  }

  onInsertClick(): void {
    this.field.path = `${SCRIPT_FIELDS_NAMESPACE}.out.${this.absolutePath}`;

    Word.run(async (context) => {
      try {
        const fieldContext: FieldContext = new FieldContext(
          this.identityStorage.get().namespace,
          SCRIPT_FIELDS_ENTITY_NAME_LABEL_PAIR,
          this.field,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          { ...systemDefaults.formatting.stringFormat },
          undefined
        );

        await this.controllerService.insert(context, fieldContext);
        await context.sync();
        this.resetField();
        this.buildAbsolutePath('');
        this.pathInputElement.nativeElement.value = '';
      } catch (err: any) {
        this.loggingService.error('ScriptFieldsComponent', 'onInsertClick', {
          message: 'Failed to insert script field.',
          error: err,
        });
        this.messageNotificationService.error('Failed to insert script field.', err);
      }
    });
  }

  onPathValueInput(e: any): void {
    this.pathValueChangesSubject.next(e.target.value);
  }

  private activeControlChanged(activeContentControl: ContentControlId | undefined): void {
    console.debug('%cscript-field - active content control changed handler', 'color:#b0b0dd');
    this.ngZone.run(() => {
      if (this.isDocusignField(activeContentControl)) {
        this.valid.setValid(false);
        return;
      }

      this.resetField();
      this.pathInputElement.nativeElement.value = '';

      const activeFieldContext = activeContentControl ? this.contentControlToFieldMapService.get(activeContentControl.id) : undefined;
      const isScriptField = this.isScriptField(activeFieldContext);
      const isContainerField = this.isContainerField(activeContentControl, activeFieldContext);
      this.activeField = isScriptField ? activeFieldContext : undefined;
      this.valid.setValid(!activeFieldContext || isScriptField || isContainerField);

      this.buildAbsolutePath('');

      if (!this.activeField) {
        this.breadcrumbs = { pathItems: [] };
        this.pathPlaceHolder = '';
        return;
      }

      // slice off 'custom.out'
      this.breadcrumbs.pathItems = this.stripNamespace(this.activeField.path).map((n) => ({ id: n, label: n, disabled: true }));
      this.pathPlaceHolder = `Start with '.' to use relative path`;
    });
  }

  private isScriptField(activeFieldContext: SerializedFieldContext | undefined): boolean {
    return activeFieldContext?.entity === SCRIPT_FIELDS_ENTITY_NAME;
  }

  private isDocusignField(activeContentControl?: ContentControlId): boolean {
    return activeContentControl ? activeContentControl.tag.startsWith('docusign-') : false;
  }

  private resetField(): void {
    this.field.label = '';
    this.field.path = '';
    this.field.type = FieldType.STRING;
  }

  private buildAbsolutePath(pathText: string): void {
    pathText = pathText.replaceAll(' ', '');
    this.absolutePath = pathText;
    if (pathText.startsWith('.') && this.activeField) {
      this.absolutePath = `${this.stripNamespace(this.activeField.path).join('.')}${pathText}`;
    }
  }

  private stripNamespace(path: string): string[] {
    const result = path.split('.');
    return path.startsWith(SCRIPT_FIELDS_NAMESPACE) ? result.slice(2) : result;
  }

  private isContainerField(activeContentControl?: ContentControlId, activeFieldContext?: SerializedFieldContext): boolean {
    if (!activeContentControl) {
      return false;
    }

    const tag: Tag = Tag.fromString(activeContentControl.tag);
    if (tag.type === TagType.Repeat || tag.type === TagType.Condition) {
      return true;
    }

    if (activeFieldContext?.dataType === FieldType.TO_ONE) {
      return true;
    }

    const fieldMeta = activeFieldContext?.path ? templateContext.getFieldByPath(activeFieldContext?.path) : undefined;
    return fieldMeta?.type === FieldType.TO_ONE;
  }
}
